import dayjs, { Dayjs } from 'dayjs';

interface ExpenseItem {
  id: string;
  description: string;
  category: ExpenseCategory;
  amount: number;
  date: Dayjs;
}

interface ExpenseReport {
  id: string;
  month: number; // 1-12
  year: number;
  title: string;
  summary?: string;
  totalAmount: number;
  fileUrl?: string;
  fileName?: string;
  items: ExpenseItem[];
  publishedDate: Dayjs;
}

type ExpenseCategory = 'servicos_publicos' | 'manutencao' | 'seguranca' | 'administrativo' | 'outros';

// Helper function to get dates relative to current date
function getRelativeDate(daysOffset: number): Dayjs {
  return dayjs().add(daysOffset, 'day');
}

function getRelativeMonth(monthsOffset: number): { month: number; year: number } {
  const date = dayjs().add(monthsOffset, 'month');
  return { month: date.month() + 1, year: date.year() };
}

export const expenseReports: ExpenseReport[] = [
  // Current month
  {
    id: 'EXP001',
    ...getRelativeMonth(0),
    title: `Despesas Mensais - ${dayjs().format('MMMM YYYY')}`,
    summary: 'Análise abrangente de todas as despesas da comunidade para o mês atual, incluindo serviços públicos, manutenção, segurança e custos administrativos.',
    totalAmount: 15875.50,
    fileUrl: '/reports/expenses-current-month.pdf',
    fileName: 'expenses-current-month.pdf',
    items: [
      { id: 'i1', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4200.00, date: getRelativeDate(-25) },
      { id: 'i2', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2100.00, date: getRelativeDate(-24) },
      { id: 'i3', description: 'Conta de Gás - Sistema de Aquecimento', category: 'servicos_publicos', amount: 1800.00, date: getRelativeDate(-23) },
      { id: 'i4', description: 'Contrato de Manutenção do Elevador', category: 'manutencao', amount: 3500.00, date: getRelativeDate(-20) },
      { id: 'i5', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-15) },
      { id: 'i6', description: 'Serviços de Limpeza', category: 'manutencao', amount: 1200.00, date: getRelativeDate(-10) },
      { id: 'i7', description: 'Material de Escritório', category: 'administrativo', amount: 275.50, date: getRelativeDate(-5) },
    ],
    publishedDate: getRelativeDate(-1),
  },

  // Last month
  {
    id: 'EXP002',
    ...getRelativeMonth(-1),
    title: `Despesas Mensais - ${dayjs().subtract(1, 'month').format('MMMM YYYY')}`,
    summary: 'Relatório detalhado de despesas para o mês anterior cobrindo todos os custos operacionais.',
    totalAmount: 14250.75,
    fileUrl: '/reports/expenses-last-month.pdf',
    fileName: 'expenses-last-month.pdf',
    items: [
      { id: 'i8', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 3800.00, date: getRelativeDate(-55) },
      { id: 'i9', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1950.00, date: getRelativeDate(-54) },
      { id: 'i10', description: 'Manutenção da Piscina', category: 'manutencao', amount: 2500.00, date: getRelativeDate(-50) },
      { id: 'i11', description: 'Atualização do Sistema de Segurança', category: 'seguranca', amount: 4000.00, date: getRelativeDate(-45) },
      { id: 'i12', description: 'Serviços de Paisagismo', category: 'manutencao', amount: 1200.00, date: getRelativeDate(-40) },
      { id: 'i13', description: 'Prêmio do Seguro', category: 'administrativo', amount: 800.75, date: getRelativeDate(-35) },
    ],
    publishedDate: getRelativeDate(-30),
  },

  // Two months ago
  {
    id: 'EXP003',
    ...getRelativeMonth(-2),
    title: `Despesas Mensais - ${dayjs().subtract(2, 'month').format('MMMM YYYY')}`,
    summary: 'Análise mensal de despesas incluindo manutenção sazonal e custos de serviços públicos.',
    totalAmount: 13500.00,
    items: [
      { id: 'i14', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 3600.00, date: getRelativeDate(-85) },
      { id: 'i15', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1800.00, date: getRelativeDate(-84) },
      { id: 'i16', description: 'Manutenção do Sistema HVAC', category: 'manutencao', amount: 3000.00, date: getRelativeDate(-80) },
      { id: 'i17', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-75) },
      { id: 'i18', description: 'Inspeção de Segurança contra Incêndio', category: 'manutencao', amount: 1500.00, date: getRelativeDate(-70) },
      { id: 'i19', description: 'Consulta Jurídica', category: 'administrativo', amount: 800.00, date: getRelativeDate(-65) },
    ],
    publishedDate: getRelativeDate(-60),
  },

  // Three months ago
  {
    id: 'EXP004',
    ...getRelativeMonth(-3),
    title: `Despesas Mensais - ${dayjs().subtract(3, 'month').format('MMMM YYYY')}`,
    summary: 'Relatório trimestral de despesas com projetos de manutenção importantes e custos operacionais regulares.',
    totalAmount: 18200.25,
    fileUrl: '/reports/expenses-q3.pdf',
    fileName: 'expenses-q3.pdf',
    items: [
      { id: 'i20', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4100.00, date: getRelativeDate(-115) },
      { id: 'i21', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2200.00, date: getRelativeDate(-114) },
      { id: 'i22', description: 'Reparos no Telhado', category: 'manutencao', amount: 8000.00, date: getRelativeDate(-110) },
      { id: 'i23', description: 'Instalação de Câmeras de Segurança', category: 'seguranca', amount: 2500.00, date: getRelativeDate(-105) },
      { id: 'i24', description: 'Renovação do Jardim', category: 'manutencao', amount: 1800.25, date: getRelativeDate(-100) },
      { id: 'i25', description: 'Serviços de Contabilidade', category: 'administrativo', amount: 600.00, date: getRelativeDate(-95) },
    ],
    publishedDate: getRelativeDate(-90),
  },

  // Four months ago
  {
    id: 'EXP005',
    ...getRelativeMonth(-4),
    title: `Despesas Mensais - ${dayjs().subtract(4, 'month').format('MMMM YYYY')}`,
    summary: 'Despesas operacionais mensais com foco na gestão de serviços públicos e segurança.',
    totalAmount: 13950.00,
    items: [
      { id: 'i26', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 3700.00, date: getRelativeDate(-145) },
      { id: 'i27', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1900.00, date: getRelativeDate(-144) },
      { id: 'i28', description: 'Serviço Anual do Elevador', category: 'manutencao', amount: 4500.00, date: getRelativeDate(-140) },
      { id: 'i29', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-135) },
      { id: 'i30', description: 'Serviços de Controle de Pragas', category: 'manutencao', amount: 800.00, date: getRelativeDate(-130) },
      { id: 'i31', description: 'Serviços de Internet', category: 'servicos_publicos', amount: 250.00, date: getRelativeDate(-125) },
    ],
    publishedDate: getRelativeDate(-120),
  },

  // Five months ago
  {
    id: 'EXP006',
    ...getRelativeMonth(-5),
    title: `Despesas Mensais - ${dayjs().subtract(5, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção de verão e custos de serviços públicos incluindo operações da piscina.',
    totalAmount: 15100.50,
    fileUrl: '/reports/expenses-summer.pdf',
    fileName: 'expenses-summer.pdf',
    items: [
      { id: 'i32', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4300.00, date: getRelativeDate(-175) },
      { id: 'i33', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2400.00, date: getRelativeDate(-174) },
      { id: 'i34', description: 'Manutenção da Piscina e Produtos Químicos', category: 'manutencao', amount: 2800.00, date: getRelativeDate(-170) },
      { id: 'i35', description: 'Manutenção do Ar Condicionado', category: 'manutencao', amount: 2200.00, date: getRelativeDate(-165) },
      { id: 'i36', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-160) },
      { id: 'i37', description: 'Despesas de Eventos da Comunidade', category: 'outros', amount: 600.50, date: getRelativeDate(-155) },
    ],
    publishedDate: getRelativeDate(-150),
  },

  // Six months ago
  {
    id: 'EXP007',
    ...getRelativeMonth(-6),
    title: `Despesas Mensais - ${dayjs().subtract(6, 'month').format('MMMM YYYY')}`,
    summary: 'Relatório de despesas de meio de ano com projetos de manutenção importantes.',
    totalAmount: 16800.00,
    items: [
      { id: 'i38', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 3900.00, date: getRelativeDate(-205) },
      { id: 'i39', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2000.00, date: getRelativeDate(-204) },
      { id: 'i40', description: 'Revestimento do Estacionamento', category: 'manutencao', amount: 7500.00, date: getRelativeDate(-200) },
      { id: 'i41', description: 'Manutenção do Sistema de Segurança', category: 'seguranca', amount: 1800.00, date: getRelativeDate(-195) },
      { id: 'i42', description: 'Inspeção dos Extintores de Incêndio', category: 'manutencao', amount: 1200.00, date: getRelativeDate(-190) },
      { id: 'i43', description: 'Taxa de Gestão da Propriedade', category: 'administrativo', amount: 2400.00, date: getRelativeDate(-185) },
    ],
    publishedDate: getRelativeDate(-180),
  },

  // Seven months ago
  {
    id: 'EXP008',
    ...getRelativeMonth(-7),
    title: `Despesas Mensais - ${dayjs().subtract(7, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção da primavera e custos operacionais regulares.',
    totalAmount: 14450.75,
    items: [
      { id: 'i44', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 3800.00, date: getRelativeDate(-235) },
      { id: 'i45', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1950.00, date: getRelativeDate(-234) },
      { id: 'i46', description: 'Paisagismo da Primavera', category: 'manutencao', amount: 3200.00, date: getRelativeDate(-230) },
      { id: 'i47', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-225) },
      { id: 'i48', description: 'Limpeza das Janelas', category: 'manutencao', amount: 1500.00, date: getRelativeDate(-220) },
      { id: 'i49', description: 'Renovação do Seguro', category: 'administrativo', amount: 1200.75, date: getRelativeDate(-215) },
    ],
    publishedDate: getRelativeDate(-210),
  },

  // Eight months ago
  {
    id: 'EXP009',
    ...getRelativeMonth(-8),
    title: `Despesas Mensais - ${dayjs().subtract(8, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção do inverno e custos de aquecimento.',
    totalAmount: 16200.00,
    fileUrl: '/reports/expenses-winter.pdf',
    fileName: 'expenses-winter.pdf',
    items: [
      { id: 'i50', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4200.00, date: getRelativeDate(-265) },
      { id: 'i51', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1800.00, date: getRelativeDate(-264) },
      { id: 'i52', description: 'Manutenção do Sistema de Aquecimento', category: 'manutencao', amount: 3500.00, date: getRelativeDate(-260) },
      { id: 'i53', description: 'Serviços de Remoção de Neve', category: 'manutencao', amount: 2800.00, date: getRelativeDate(-255) },
      { id: 'i54', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-250) },
      { id: 'i55', description: 'Combustível do Gerador de Emergência', category: 'servicos_publicos', amount: 1100.00, date: getRelativeDate(-245) },
    ],
    publishedDate: getRelativeDate(-240),
  },

  // Nine months ago
  {
    id: 'EXP010',
    ...getRelativeMonth(-9),
    title: `Despesas Mensais - ${dayjs().subtract(9, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção de fim de ano e custos administrativos.',
    totalAmount: 17550.25,
    items: [
      { id: 'i56', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4100.00, date: getRelativeDate(-295) },
      { id: 'i57', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2000.00, date: getRelativeDate(-294) },
      { id: 'i58', description: 'Inspeção Anual do Edifício', category: 'manutencao', amount: 4500.00, date: getRelativeDate(-290) },
      { id: 'i59', description: 'Serviço Anual do Sistema de Segurança', category: 'seguranca', amount: 2200.00, date: getRelativeDate(-285) },
      { id: 'i60', description: 'Limpeza de Fim de Ano', category: 'manutencao', amount: 1800.00, date: getRelativeDate(-280) },
      { id: 'i61', description: 'Serviços de Auditoria', category: 'administrativo', amount: 2950.25, date: getRelativeDate(-275) },
    ],
    publishedDate: getRelativeDate(-270),
  },

  // Ten months ago
  {
    id: 'EXP011',
    ...getRelativeMonth(-10),
    title: `Despesas Mensais - ${dayjs().subtract(10, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção do outono e preparação para o inverno.',
    totalAmount: 14800.00,
    items: [
      { id: 'i62', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 3900.00, date: getRelativeDate(-335) },
      { id: 'i63', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1900.00, date: getRelativeDate(-334) },
      { id: 'i64', description: 'Limpeza das Calhas', category: 'manutencao', amount: 1200.00, date: getRelativeDate(-330) },
      { id: 'i65', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-325) },
      { id: 'i66', description: 'Paisagismo do Outono', category: 'manutencao', amount: 2500.00, date: getRelativeDate(-320) },
      { id: 'i67', description: 'Preparação do Sistema de Aquecimento', category: 'manutencao', amount: 2500.00, date: getRelativeDate(-315) },
    ],
    publishedDate: getRelativeDate(-300),
  },

  // Eleven months ago
  {
    id: 'EXP012',
    ...getRelativeMonth(-11),
    title: `Despesas Mensais - ${dayjs().subtract(11, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção do final do verão e custos de serviços públicos.',
    totalAmount: 15350.75,
    fileUrl: '/reports/expenses-late-summer.pdf',
    fileName: 'expenses-late-summer.pdf',
    items: [
      { id: 'i68', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4200.00, date: getRelativeDate(-365) },
      { id: 'i69', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2300.00, date: getRelativeDate(-364) },
      { id: 'i70', description: 'Serviços de Fechamento da Piscina', category: 'manutencao', amount: 1800.00, date: getRelativeDate(-360) },
      { id: 'i71', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-355) },
      { id: 'i72', description: 'Retoques na Pintura Externa', category: 'manutencao', amount: 3200.75, date: getRelativeDate(-350) },
      { id: 'i73', description: 'Boletim da Comunidade', category: 'administrativo', amount: 50.00, date: getRelativeDate(-345) },
    ],
    publishedDate: getRelativeDate(-330),
  },

  // Twelve months ago
  {
    id: 'EXP013',
    ...getRelativeMonth(-12),
    title: `Despesas Mensais - ${dayjs().subtract(12, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção anual e projetos importantes de reparo.',
    totalAmount: 19800.00,
    items: [
      { id: 'i74', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4000.00, date: getRelativeDate(-395) },
      { id: 'i75', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2000.00, date: getRelativeDate(-394) },
      { id: 'i76', description: 'Reparos Importantes de Encanamento', category: 'manutencao', amount: 8500.00, date: getRelativeDate(-390) },
      { id: 'i77', description: 'Substituição de Câmeras de Segurança', category: 'seguranca', amount: 3500.00, date: getRelativeDate(-385) },
      { id: 'i78', description: 'Controle Anual de Pragas', category: 'manutencao', amount: 1200.00, date: getRelativeDate(-380) },
      { id: 'i79', description: 'Serviços Jurídicos', category: 'administrativo', amount: 600.00, date: getRelativeDate(-375) },
    ],
    publishedDate: getRelativeDate(-360),
  },

  // Thirteen months ago
  {
    id: 'EXP014',
    ...getRelativeMonth(-13),
    title: `Despesas Mensais - ${dayjs().subtract(13, 'month').format('MMMM YYYY')}`,
    summary: 'Custos de pico de serviços públicos no verão e manutenção.',
    totalAmount: 16950.50,
    items: [
      { id: 'i80', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4800.00, date: getRelativeDate(-425) },
      { id: 'i81', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2500.00, date: getRelativeDate(-424) },
      { id: 'i82', description: 'Serviços de Abertura da Piscina', category: 'manutencao', amount: 2000.00, date: getRelativeDate(-420) },
      { id: 'i83', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-415) },
      { id: 'i84', description: 'Reparos do Ar Condicionado', category: 'manutencao', amount: 3800.00, date: getRelativeDate(-410) },
      { id: 'i85', description: 'Despesas de Eventos de Verão', category: 'outros', amount: 1050.50, date: getRelativeDate(-405) },
    ],
    publishedDate: getRelativeDate(-390),
  },

  // Fourteen months ago
  {
    id: 'EXP015',
    ...getRelativeMonth(-14),
    title: `Despesas Mensais - ${dayjs().subtract(14, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção da primavera e preparação para o verão.',
    totalAmount: 15600.00,
    fileUrl: '/reports/expenses-spring.pdf',
    fileName: 'expenses-spring.pdf',
    items: [
      { id: 'i86', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4100.00, date: getRelativeDate(-455) },
      { id: 'i87', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2100.00, date: getRelativeDate(-454) },
      { id: 'i88', description: 'Paisagismo da Primavera', category: 'manutencao', amount: 3500.00, date: getRelativeDate(-450) },
      { id: 'i89', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-445) },
      { id: 'i90', description: 'Reparos na Iluminação Externa', category: 'manutencao', amount: 1800.00, date: getRelativeDate(-440) },
      { id: 'i91', description: 'Revisão do Seguro', category: 'administrativo', amount: 1300.00, date: getRelativeDate(-435) },
    ],
    publishedDate: getRelativeDate(-420),
  },

  // Fifteen months ago
  {
    id: 'EXP016',
    ...getRelativeMonth(-15),
    title: `Despesas Mensais - ${dayjs().subtract(15, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção do inverno e custos de aquecimento.',
    totalAmount: 17400.25,
    items: [
      { id: 'i92', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4300.00, date: getRelativeDate(-485) },
      { id: 'i93', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1900.00, date: getRelativeDate(-484) },
      { id: 'i94', description: 'Reparos do Sistema de Aquecimento', category: 'manutencao', amount: 4200.00, date: getRelativeDate(-480) },
      { id: 'i95', description: 'Serviços de Remoção de Neve', category: 'manutencao', amount: 3200.00, date: getRelativeDate(-475) },
      { id: 'i96', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-470) },
      { id: 'i97', description: 'Reparos de Emergência', category: 'manutencao', amount: 1000.25, date: getRelativeDate(-465) },
    ],
    publishedDate: getRelativeDate(-450),
  },

  // Sixteen months ago
  {
    id: 'EXP017',
    ...getRelativeMonth(-16),
    title: `Despesas Mensais - ${dayjs().subtract(16, 'month').format('MMMM YYYY')}`,
    summary: 'Despesas de fim de ano e manutenção anual.',
    totalAmount: 18900.00,
    items: [
      { id: 'i98', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4200.00, date: getRelativeDate(-515) },
      { id: 'i99', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2000.00, date: getRelativeDate(-514) },
      { id: 'i100', description: 'Manutenção Anual do Edifício', category: 'manutencao', amount: 8500.00, date: getRelativeDate(-510) },
      { id: 'i101', description: 'Serviço Anual do Sistema de Segurança', category: 'seguranca', amount: 2500.00, date: getRelativeDate(-505) },
      { id: 'i102', description: 'Limpeza de Fim de Ano', category: 'manutencao', amount: 2000.00, date: getRelativeDate(-500) },
      { id: 'i103', description: 'Auditoria Anual', category: 'administrativo', amount: 2700.00, date: getRelativeDate(-495) },
    ],
    publishedDate: getRelativeDate(-480),
  },

  // Seventeen months ago
  {
    id: 'EXP018',
    ...getRelativeMonth(-17),
    title: `Despesas Mensais - ${dayjs().subtract(17, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção do outono e preparação para o inverno.',
    totalAmount: 15200.75,
    items: [
      { id: 'i104', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4000.00, date: getRelativeDate(-545) },
      { id: 'i105', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 1950.00, date: getRelativeDate(-544) },
      { id: 'i106', description: 'Manutenção das Calhas', category: 'manutencao', amount: 1500.00, date: getRelativeDate(-540) },
      { id: 'i107', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-535) },
      { id: 'i108', description: 'Paisagismo do Outono', category: 'manutencao', amount: 2800.00, date: getRelativeDate(-530) },
      { id: 'i109', description: 'Preparação do Sistema de Aquecimento', category: 'manutencao', amount: 2150.75, date: getRelativeDate(-525) },
    ],
    publishedDate: getRelativeDate(-510),
  },

  // Eighteen months ago
  {
    id: 'EXP019',
    ...getRelativeMonth(-18),
    title: `Despesas Mensais - ${dayjs().subtract(18, 'month').format('MMMM YYYY')}`,
    summary: 'Manutenção do final do verão e custos de serviços públicos.',
    totalAmount: 16100.00,
    fileUrl: '/reports/expenses-late-summer-2.pdf',
    fileName: 'expenses-late-summer-2.pdf',
    items: [
      { id: 'i110', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 4500.00, date: getRelativeDate(-575) },
      { id: 'i111', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2400.00, date: getRelativeDate(-574) },
      { id: 'i112', description: 'Manutenção da Piscina', category: 'manutencao', amount: 2800.00, date: getRelativeDate(-570) },
      { id: 'i113', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-565) },
      { id: 'i114', description: 'Reparos Externos', category: 'manutencao', amount: 2800.00, date: getRelativeDate(-560) },
      { id: 'i115', description: 'Evento da Comunidade', category: 'outros', amount: 800.00, date: getRelativeDate(-555) },
    ],
    publishedDate: getRelativeDate(-540),
  },

  // Nineteen months ago
  {
    id: 'EXP020',
    ...getRelativeMonth(-19),
    title: `Despesas Mensais - ${dayjs().subtract(19, 'month').format('MMMM YYYY')}`,
    summary: 'Custos de pico de serviços públicos no meio do verão e manutenção.',
    totalAmount: 17800.50,
    items: [
      { id: 'i116', description: 'Conta de Eletricidade - Áreas Comuns', category: 'servicos_publicos', amount: 5200.00, date: getRelativeDate(-605) },
      { id: 'i117', description: 'Conta de Água - Áreas Comuns', category: 'servicos_publicos', amount: 2600.00, date: getRelativeDate(-604) },
      { id: 'i118', description: 'Manutenção do Ar Condicionado', category: 'manutencao', amount: 3500.00, date: getRelativeDate(-600) },
      { id: 'i119', description: 'Salários dos Guardas de Segurança', category: 'seguranca', amount: 2800.00, date: getRelativeDate(-595) },
      { id: 'i120', description: 'Reparos dos Equipamentos da Piscina', category: 'manutencao', amount: 2200.00, date: getRelativeDate(-590) },
      { id: 'i121', description: 'Despesas de Eventos de Verão', category: 'outros', amount: 1500.50, date: getRelativeDate(-585) },
    ],
    publishedDate: getRelativeDate(-570),
  },
]; 